import * as React from 'react';
import { Popup } from '../popover/Popup';
import * as Classes from '../common/classes';
import { Icon } from '../icon/Icon';
import { mcFilterConfig, mcFilterConfigClear } from '@mc/react-ui-icons';
import { TableColumn } from '../table/TableProps';
import {
  MutableRefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DataTableColumnFilters } from './DataTable';
import {
  filtersClearFieldInitial,
  filtersComputeFormInitialValues,
  filtersComputeQueryFilter,
  filtersIsFieldValueFilled,
  filtersValuesChanged,
  getFilterComponent,
} from '../filter/Filters';
import { IData } from '../virtual-data/VirtualData';
import { Form } from 'react-final-form';
import { Divider } from '../divider/Divider';
import { Button } from '../button/Button';
import { VF } from '../index';
import { OnChange } from 'react-final-form-listeners';

export type ColumnFilterProps<DATA extends IData<ID>, FORM, ID> = {
  column: TableColumn<DATA>;
  filters: DataTableColumnFilters<FORM>;
};

export const ColumnFilter = <
  DATA extends IData<ID>,
  FORM extends unknown,
  ID extends unknown,
>({
  filters,
  column,
}: ColumnFilterProps<DATA, FORM, ID>) => {
  const filledValueIndex = useMemo(() => {
    if (column.filters) {
      for (let a = 0; a < column.filters.length; a++) {
        const filterDef = column.filters[a];

        if (
          filtersIsFieldValueFilled(filters.type, filters.formFilter, filterDef)
        ) {
          return a;
        }
      }
    }
    return undefined;
  }, [column, filters.formFilter]);

  const [isOpen, setOpen] = useState(false);

  const refSubmit = useRef<any>(undefined);

  return (
    <Popup
      open={isOpen}
      onOpen={() => {
        setOpen(true);
      }}
      // closeOnDocumentClick={false}
      // closeOnEscape={false}
      onClose={() => {
        refSubmit.current && refSubmit.current();
        setOpen(false);
      }}
      arrow
      mode={'edit'}
      trigger={
        <div className={Classes.DATA_TABLE_HEADER_CELL_FILTER}>
          <Icon
            icon={
              filledValueIndex === undefined
                ? mcFilterConfigClear
                : mcFilterConfig
            }
          />
        </div>
      }
    >
      {(onClose) => {
        return (
          <ColumnFilterForm
            refSubmit={refSubmit}
            column={column}
            filters={filters}
            onClose={() => {
              refSubmit.current = undefined;
              onClose();
            }}
            filledValueIndex={filledValueIndex}
          />
        );
      }}
    </Popup>
  );
};

export type ColumnFilterFormProps<DATA extends IData<ID>, FORM, ID> = {
  column: TableColumn<DATA>;
  filters: DataTableColumnFilters<FORM>;
  filledValueIndex?: number;
  onClose: () => void;
  refSubmit: MutableRefObject<() => void>;
};

const FILTER_FIELD_FORM_ATTR_NAME = '_filterField_';

export const ColumnFilterForm = <
  DATA extends IData<ID>,
  FORM extends unknown,
  ID extends unknown,
>({
  column,
  filters,
  onClose,
  filledValueIndex,
  refSubmit,
}: ColumnFilterFormProps<DATA, FORM, ID>) => {
  const filtersDef = column.filters;

  if (!filtersDef) {
    return <div></div>;
  }

  let filterType = filters.type || 'default';

  const handleZrusitFiltr = useCallback(() => {
    // @ts-ignore
    const formData = { ...filters.formFilter };

    if (filtersDef) {
      for (const filterDef of filtersDef) {
        filtersClearFieldInitial(filterType, formData, filterDef);
      }
    }

    // @ts-ignore
    const queryData = filtersComputeQueryFilter(
      filterType,
      formData,
      filtersDef
    );
    filters.setFilter(formData, queryData);
    onClose();
  }, [filters.formFilter, filtersDef, filterType]);

  const formInitialValues = useMemo(() => {
    let formInititalValues = filtersComputeFormInitialValues(
      filterType,
      filters.formFilter,
      filtersDef
    );

    if (filtersDef.length > 1) {
      formInititalValues[FILTER_FIELD_FORM_ATTR_NAME] = `i${
        filledValueIndex || 0
      }`;
    }

    return formInititalValues;
  }, [filters.formFilter, filtersDef, filterType]);

  const doFilter = useCallback(
    (formData: any) => {
      const { [FILTER_FIELD_FORM_ATTR_NAME]: prevSelectedIndexStr } =
        formInitialValues;
      const {
        [FILTER_FIELD_FORM_ATTR_NAME]: newSelectedIndexStr,
        ...realFormData
      } = formData;

      // Pokud nebyla hodnota změněna, neposíláme set filtr aby při zavření nedocházelo k refetch dat
      let newFilterDef;
      let prevFilterDef;
      if (filtersDef.length > 1) {
        const newSelectedIndex = Number(newSelectedIndexStr.substring(1));
        const prevSelectedIndex = Number(prevSelectedIndexStr.substring(1));
        newFilterDef = filtersDef[newSelectedIndex];
        prevFilterDef = filtersDef[prevSelectedIndex];
      } else {
        newFilterDef = filtersDef[0];
        prevFilterDef = filtersDef[0];
      }
      if (
        filtersValuesChanged(
          filterType,
          prevFilterDef,
          formInitialValues,
          newFilterDef,
          realFormData
        )
      ) {
        // @ts-ignore
        const queryData = filtersComputeQueryFilter(
          filterType,
          realFormData,
          filtersDef
        );
        filters.setFilter(realFormData, queryData);
      }
    },
    [filterType, filtersDef, filters]
  );

  if (filtersDef.length > 1) {
    // více možností - více typů položek dle kterých se filtruje - výběr dle které pomocí radio buttonu
    return (
      <Form<FORM>
        onSubmit={(formData) => {
          doFilter(formData);
          onClose();
        }}
        initialValues={formInitialValues}
      >
        {(props) => {
          refSubmit.current = () => {
            props.handleSubmit();
          };

          const selectedFieldIndex = Number(
            // @ts-ignore
            props.values[FILTER_FIELD_FORM_ATTR_NAME].substring(1)
          );
          const filterDef = filtersDef[selectedFieldIndex];
          const valueFilled = filtersIsFieldValueFilled(
            filters.type,
            props.values,
            filterDef
          );

          let component = getFilterComponent(
            { ...filterDef, label: undefined },
            filterType
          );

          const res = component({
            filterDef: filterDef,
            onSearch: props.handleSubmit,
          });

          return (
            <div className={Classes.DATA_TABLE_COLUMN_FILTER_FORM}>
              <div className={Classes.DATA_TABLE_COLUMN_FILTER_FORM_BODY}>
                <VF.RadioGroup
                  name={FILTER_FIELD_FORM_ATTR_NAME}
                  horizontal
                  inlineValidation
                >
                  {filtersDef?.map((cf, index) => {
                    return (
                      <VF.Radio
                        key={index}
                        label={cf.label}
                        name={FILTER_FIELD_FORM_ATTR_NAME}
                        value={`i${index}`}
                      />
                    );
                  })}
                </VF.RadioGroup>
                <OnChange name={FILTER_FIELD_FORM_ATTR_NAME}>
                  {(_, previous) => {
                    if (column.filters) {
                      const index = Number(previous.substring(1));
                      const formData = {};
                      let filterDef = column.filters[index];
                      filtersClearFieldInitial(filterType, formData, filterDef);
                      props.form.change(
                        // @ts-ignore
                        filterDef.name,
                        // @ts-ignore
                        formData[filterDef.name]
                      );
                    }
                  }}
                </OnChange>
                {res}
              </div>
              <Divider horizontal />
              <div className={Classes.DATA_TABLE_COLUMN_FILTER_FORM_FOOTER}>
                <Button
                  onClick={handleZrusitFiltr}
                  disabled={props.submitting || !valueFilled}
                  dataTest={'cancel'}
                >
                  Zrušit filtr
                </Button>
                <Button
                  onClick={props.handleSubmit}
                  style={'primary'}
                  dataTest={'ok'}
                >
                  OK
                </Button>
              </div>
            </div>
          );
        }}
      </Form>
    );
  } else {
    // jedna možnost - jeden typ fieldu pro filtr
    const filterDef = filtersDef[0];

    return (
      <Form<FORM>
        onSubmit={(formData) => {
          doFilter(formData);
          onClose();
        }}
        initialValues={formInitialValues}
      >
        {(props) => {
          let component = getFilterComponent(
            { ...filterDef, label: undefined },
            filterType
          );

          refSubmit.current = () => {
            props.handleSubmit();
          };

          const valueFilled = filtersIsFieldValueFilled(
            filters.type,
            props.values,
            filterDef
          );

          const res = component({
            filterDef: filterDef,
            onSearch: props.handleSubmit,
          });

          return (
            <div className={Classes.DATA_TABLE_COLUMN_FILTER_FORM}>
              <div className={Classes.DATA_TABLE_COLUMN_FILTER_FORM_BODY}>
                {res}
              </div>
              <Divider horizontal />
              <div className={Classes.DATA_TABLE_COLUMN_FILTER_FORM_FOOTER}>
                <Button
                  onClick={handleZrusitFiltr}
                  disabled={props.submitting || !valueFilled}
                  dataTest={'cancel'}
                >
                  Zrušit filtr
                </Button>
                <Button
                  onClick={props.handleSubmit}
                  style={'primary'}
                  dataTest={'ok'}
                >
                  OK
                </Button>
              </div>
            </div>
          );
        }}
      </Form>
    );
  }
};
